/**
 * The rules behind the phone Pour Dashboard, as pure functions over the
 * dashboard reply (no React, no API calls, no UI text: the screens word
 * these results).
 *
 *  - locationHealth: one location's state ('over_limit', 'offline', ...),
 *    its breaches, limits, battery and signal, and how urgent it is.
 *  - pourPhase, pourCounts, overviewSections: the status strip and the
 *    Overview's order (Needs attention first).
 *  - nextTarget, pourMaturitySummary, worstDifferential: the maturity and
 *    mass-concrete lines.
 *  - chartPickerOptions, locationChartKinds, locationChartNeed and the
 *    overlay helpers: which charts to offer and what to load for them.
 *
 * Freshness is judged against `refNow`, the time of the last successful
 * fetch, not the wall clock: a phone that lost its connection 40 minutes
 * ago must not turn every logger "Offline". The shared rules are reused,
 * never re-implemented: freshness (utils/onlineStatus), the limit
 * comparison (limitLines.breaksLimit), maturity locations
 * (utils/locationMaturity), target status (utils/targetStatus), strength
 * display (utils/maturityDisplay), battery and signal (utils/loggerHealth)
 * and the function order (utils/locationChannels.orderByFunction).
 */
import type {
  ChartDataResponse,
  ChartSeriesKey,
  DashboardResponse,
  LimitBreach,
  LocationStatus,
  MaturityDescriptor,
  Pour2Limit,
  PourSummary,
  Target,
  TempLimit,
} from '../../../services/pours';
import { parseUtc } from '../../../utils/datetime';
import type { IsoLike } from '../../../utils/datetime';
import {
  isAmbientChannel,
  isSiteConditionsChannel,
  isWeatherLocation,
  orderByFunction,
} from '../../../utils/locationChannels';
import { locationHasMaturity } from '../../../utils/locationMaturity';
import { batteryPercent, isBatteryLow } from '../../../utils/loggerHealth';
import {
  formatMaturityWithIndex,
  formatStrengthReading,
  maturityMeta,
  pourHasCurve,
  resolveStrengthReading,
  resolveStrengthUnit,
  targetPurpose,
  targetStrengthText,
} from '../../../utils/maturityDisplay';
import {
  isLoggingEnded,
  locationOnlineStatus,
  pourStatusEnded,
  type OnlineStatus,
} from '../../../utils/onlineStatus';
import type { PourFunctionFlags } from '../../../utils/pourFunctionality';
import { earliestTime, pourClockStart, type PourClockStart } from '../../../utils/relativeTime';
import { linkedSiteWeather } from '../../../utils/siteConditions';
import { targetStatusText, type TargetStatus } from '../../../utils/targetStatus';
import { LIMIT_OPERATOR_SYMBOLS, breaksLimit } from '../limitLines';
import type { MultiChartMetric } from '../overlayModel';
import type { PhoneLocationChartKind } from './phoneChartOptions';

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Epoch ms of a backend time (naive UTC), or null. */
const toMs = (value: IsoLike): number | null => {
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** A lower-bound operator ('<', '<='): breaking it is "Under limit". */
export const isLowerOperator = (operator: string | null | undefined): boolean =>
  (LIMIT_OPERATOR_SYMBOLS[operator ?? ''] ?? operator ?? '').startsWith('<');

// ---------------------------------------------------------------------------
// One location's health
// ---------------------------------------------------------------------------

/** A location's state, most urgent first in the Overview:
 *  over_limit / under_limit  outside a temperature or differential limit now;
 *  offline / delayed         the freshness bands (judged at the last fetch);
 *  sensor                    the logger reports a fault (heartbeat warning or
 *                            error), its readings still fresh;
 *  awaiting                  started, no reading yet;
 *  ok                        a concrete channel reporting, within its limits;
 *  no_limits                 a concrete channel reporting with no limit set
 *                            (never "OK": nothing was checked);
 *  reporting                 a site or humidity channel reporting (they have
 *                            no limits, so never "OK" either);
 *  scheduled / ended         before its start / after its logging ended. */
export type LocationAlert =
  | 'over_limit'
  | 'under_limit'
  | 'offline'
  | 'delayed'
  | 'sensor'
  | 'awaiting'
  | 'ok'
  | 'no_limits'
  | 'reporting'
  | 'scheduled'
  | 'ended';

/** Overview order of the Needs attention group (lower first). */
export const ATTENTION_SEVERITY = {
  outsideLimits: 0,
  offline: 1,
  delayed: 2,
  sensor: 3,
  lowBattery: 4,
} as const;
/** The severity of a location that needs no attention. */
export const NO_ATTENTION = 9;

/** The last reading of an Offline location broke a limit: shown as
 *  "Offline · last reading 78.3 °C was over Max Temp, 3 h ago", counted as
 *  not reporting, never as outside limits now. */
export interface StaleOver {
  kind: LimitBreach['kind'];
  value: number;
  limitName: string | null;
  operator: string;
  limitValue: number;
  /** When that reading was taken (epoch ms); null when unknown. */
  at: number | null;
}

export interface LocationHealth {
  alert: LocationAlert;
  /** Freshness at the last fetch (locationOnlineStatus). */
  online: OnlineStatus;
  /** Temperature and differential limits broken now, worst first; empty
   *  when not judged (stale, ended, scheduled, site channels). */
  breaches: LimitBreach[];
  /** A concrete channel: limits apply (site and humidity channels never). */
  concrete: boolean;
  /** Some temperature or differential limit applies to the location. */
  hasLimits: boolean;
  /** The temperature limits the profile bar, its caption and the verdict
   *  use: the location's own (dashboard site state), else the pour's
   *  Criteria '02' rows. */
  limits: TempLimit[];
  /** The limit the card's caption names ("Limit 75 °C"): the highest upper
   *  limit, else the lowest lower one; null without limits. */
  limitCaption: TempLimit | null;
  staleOver?: StaleOver;
  /** Outside the evaporation limit now (its own flag in v1, not counted
   *  with the limits). */
  evaporationOver: boolean;
  /** The logger reports a fault (heartbeat 'warning' or 'error'), counted
   *  as the Control Panel's WarningCount counts it. */
  sensorIssue: boolean;
  batteryPct: number | null;
  batteryLow: boolean;
  /** ATTENTION_SEVERITY, or NO_ATTENTION. */
  severity: number;
  /** In the Needs attention group. */
  attention: boolean;
  /** The verdict came from the dashboard's site state (site_state=true);
   *  false: the client's fallback (temperature only, pour limits). */
  fromSiteState: boolean;
}

/** A pour limit row as the phone uses it. */
const tempLimitFromPour = (limit: Pour2Limit): TempLimit => ({
  name: limit.Purpose || null,
  operator: LIMIT_OPERATOR_SYMBOLS[limit.LimitsType] || '>=',
  value: Number(limit.LimitsTemp),
  color: limit.BackgroundColor || null,
});

/** The pour's temperature limits (Criteria '02'), as TempLimit rows. */
export const pourTempLimits = (pourLimits: ReadonlyArray<Pour2Limit> | null | undefined): TempLimit[] =>
  (pourLimits ?? [])
    .filter((l) => l.Criteria === '02')
    .map(tempLimitFromPour)
    .filter((l) => Number.isFinite(l.value));

/** The limit a caption names: the highest upper limit, else the lowest
 *  lower limit. */
export const limitCaptionOf = (limits: ReadonlyArray<TempLimit>): TempLimit | null => {
  const upper = limits.filter((l) => !isLowerOperator(l.operator));
  if (upper.length > 0) return upper.reduce((a, b) => (b.value > a.value ? b : a));
  const lower = limits.filter((l) => isLowerOperator(l.operator));
  return lower.length > 0 ? lower.reduce((a, b) => (b.value < a.value ? b : a)) : null;
};

/** The client's fallback verdict: the current temperature against the
 *  limits, worst (furthest past its limit) first. */
const fallbackBreaches = (loc: LocationStatus, limits: ReadonlyArray<TempLimit>): LimitBreach[] => {
  const value = finite(loc.current_temp);
  if (value === null) return [];
  return limits
    .filter((l) => breaksLimit(value, l.operator, l.value))
    .map((l): LimitBreach => ({
      kind: 'temperature',
      limit_name: l.name,
      operator: l.operator,
      limit_value: l.value,
      value,
      value_at: loc.last_read ?? null,
      since: null,
      since_capped: false,
      color: l.color,
      partner_id: null,
      partner_name: null,
    }))
    .sort((a, b) => Math.abs(b.value - b.limit_value) - Math.abs(a.value - a.limit_value));
};

/**
 * One location's health at `refNow`, the time of the last successful
 * fetch. Uses the dashboard's site state when sent (over_limit, stale,
 * breaches, temp_limits, has_temp_limits, differentials, evaporation_over,
 * battery_pct); without it, judges the temperature only: a concrete
 * channel's current_temp against the pour's Criteria '02' rows.
 *
 * Never outside limits when ended, scheduled, a site or humidity channel,
 * or when the reading is stale (Offline): then the alert is 'offline' and
 * `staleOver` says which limit the last reading broke.
 */
export const locationHealth = (
  loc: LocationStatus,
  pour: Pick<PourSummary, 'status'> | null | undefined,
  pourLimits: ReadonlyArray<Pour2Limit> | null | undefined,
  refNow: number,
): LocationHealth => {
  const online = locationOnlineStatus(loc, pour?.status ?? null, refNow);
  const concrete = !isSiteConditionsChannel(loc);
  const fromSiteState =
    loc.over_limit !== undefined || loc.breaches !== undefined || loc.temp_limits !== undefined;

  const limits = !concrete
    ? []
    : loc.temp_limits !== undefined
      ? (loc.temp_limits ?? []).filter((l) => Number.isFinite(Number(l.value)))
      : pourTempLimits(pourLimits);
  const differentialLimits = concrete && (loc.differentials ?? []).some((d) => d.limit_value != null);
  const hasLimits = concrete && (
    (loc.has_temp_limits !== undefined ? !!loc.has_temp_limits : limits.length > 0) || differentialLimits
  );

  // The limits the newest reading breaks (temperature and differential).
  let raw: LimitBreach[] = [];
  if (concrete) {
    if (fromSiteState) {
      raw = (loc.breaches ?? []).filter((b) => b.kind !== 'evaporation');
      if (raw.length === 0 && loc.over_limit === true) raw = fallbackBreaches(loc, limits);
    } else {
      raw = fallbackBreaches(loc, limits);
    }
  }

  const ended = online.level === 'ended';
  const scheduled = online.level === 'scheduled';
  const judged = !ended && !scheduled;
  const stale = loc.stale === true || online.level === 'offline';
  const breaches = judged && !stale ? raw : [];
  const worst = raw[0];
  const staleOver: StaleOver | undefined = judged && stale && worst
    ? {
        kind: worst.kind,
        value: worst.value,
        limitName: worst.limit_name,
        operator: worst.operator,
        limitValue: worst.limit_value,
        at: toMs(worst.value_at ?? loc.last_read),
      }
    : undefined;

  const status = (loc.status ?? '').trim().toLowerCase();
  const sensorIssue = status === 'warning' || status === 'error';
  const batteryPct = batteryPercent(loc);

  let alert: LocationAlert;
  if (ended) alert = 'ended';
  else if (scheduled) alert = 'scheduled';
  else if (breaches.length > 0) alert = isLowerOperator(breaches[0].operator) ? 'under_limit' : 'over_limit';
  else if (online.level === 'offline') alert = 'offline';
  else if (online.awaiting) alert = 'awaiting';
  else if (online.level === 'delayed') alert = 'delayed';
  else if (sensorIssue) alert = 'sensor';
  else if (!concrete) alert = 'reporting';
  else if (!hasLimits) alert = 'no_limits';
  else alert = 'ok';

  const batteryLow = isBatteryLow(batteryPct);
  let severity: number = NO_ATTENTION;
  if (alert === 'over_limit' || alert === 'under_limit') severity = ATTENTION_SEVERITY.outsideLimits;
  else if (alert === 'offline') severity = ATTENTION_SEVERITY.offline;
  else if (alert === 'delayed') severity = ATTENTION_SEVERITY.delayed;
  else if (alert === 'sensor') severity = ATTENTION_SEVERITY.sensor;
  else if (judged && batteryLow) severity = ATTENTION_SEVERITY.lowBattery;

  return {
    alert,
    online,
    breaches,
    concrete,
    hasLimits,
    limits,
    limitCaption: limitCaptionOf(limits),
    ...(staleOver ? { staleOver } : {}),
    evaporationOver: concrete && judged && !stale && loc.evaporation_over === true,
    sensorIssue,
    batteryPct,
    batteryLow,
    severity,
    attention: severity !== NO_ATTENTION,
    fromSiteState,
  };
};

/** Every location's health, by location id. */
export const locationHealthMap = (
  data: Pick<DashboardResponse, 'pour' | 'locations'>,
  pourLimits: ReadonlyArray<Pour2Limit> | null | undefined,
  refNow: number,
): Map<string, LocationHealth> => {
  const map = new Map<string, LocationHealth>();
  (data.locations ?? []).forEach((loc) => {
    map.set(loc.id, locationHealth(loc, data.pour, pourLimits, refNow));
  });
  return map;
};

// ---------------------------------------------------------------------------
// The pour: phase and counts
// ---------------------------------------------------------------------------

/** The status strip's phase line:
 *  no_locations  no monitoring location is set up (e.g. a Pending pour);
 *  scheduled     waiting for its planned start;
 *  awaiting      started, no reading yet;
 *  monitoring    readings arriving;
 *  ended         the pour is complete or every location's logging ended. */
export type PourPhase = 'no_locations' | 'scheduled' | 'awaiting' | 'monitoring' | 'ended';

export interface PourPhaseInfo {
  phase: PourPhase;
  /** What the "since start" clock counts from, and which start it is (the
   *  Control Panel card's clock: pourClockStart). */
  clock: PourClockStart | null;
  /** Scheduled: the planned start (epoch ms). */
  startsAt: number | null;
  /** The newest reading of any location (epoch ms). */
  newestReadingAt: number | null;
  /** Ended: when the last location's logging ended (epoch ms), if known. */
  endedAt: number | null;
}

/** The newest reading of the pour: the dashboard's newest_reading_at, else
 *  the newest location last_read (epoch ms); null before any reading. */
export const newestReadingAt = (data: Pick<DashboardResponse, 'pour' | 'locations'>): number | null => {
  const sent = toMs(data.pour?.newest_reading_at);
  if (sent !== null) return sent;
  let newest: number | null = null;
  (data.locations ?? []).forEach((l) => {
    const t = toMs(l.last_read);
    if (t !== null && (newest === null || t > newest)) newest = t;
  });
  return newest;
};

/** The pour's phase at `refNow` (the last successful fetch). */
export const pourPhase = (
  data: Pick<DashboardResponse, 'pour' | 'locations' | 'site_weather'>,
  refNow: number,
): PourPhaseInfo => {
  const pour = data.pour;
  const locations = data.locations ?? [];
  const newest = newestReadingAt(data);
  // The Control Panel card's inputs (pourClockStart), so the card and the
  // strip count from the same start. The card's LoggerStart is the pour's,
  // else the earliest location LogginStart, which the summary sends as each
  // location's scheduled_start.
  const clock = pourClockStart({
    started: pour?.started_at,
    // The earliest location concrete start, as the backend's concrete_start.
    concreteStart: pour?.concrete_start ?? earliestTime(locations.map((l) => l.ea_start)),
    loggerStart: earliestTime(locations.map((l) => l.scheduled_start)),
  });
  let endedAt: number | null = null;
  locations.forEach((l) => {
    const t = toMs(l.logging_ended);
    if (t !== null && t <= refNow && (endedAt === null || t > endedAt)) endedAt = t;
  });
  const startsAt = toMs(pour?.starts_at) ?? earliestTime(locations.map((l) => l.scheduled_start));
  const base = { clock, newestReadingAt: newest, startsAt: null as number | null, endedAt: null as number | null };

  if (locations.length === 0) {
    // A weather station is site weather, never a location: a pour that
    // records only site weather is monitored by its station (the backend's
    // scheduled and newest reading follow it). A station that is not linked
    // records nothing for the pour.
    const site = linkedSiteWeather(data.site_weather);
    if (!site) return { ...base, phase: 'no_locations' };
    if (pourStatusEnded(pour?.status)) return { ...base, phase: 'ended', endedAt };
    if (pour?.scheduled === true) return { ...base, phase: 'scheduled', startsAt: toMs(pour?.starts_at) ?? toMs(site.starts_at) };
    return { ...base, phase: newest === null ? 'awaiting' : 'monitoring' };
  }
  if (pourStatusEnded(pour?.status) || locations.every((l) => isLoggingEnded(l, pour?.status, refNow))) {
    return { ...base, phase: 'ended', endedAt };
  }
  const allScheduled = locations.every(
    (l) => locationOnlineStatus(l, pour?.status, refNow).level === 'scheduled',
  );
  if (pour?.scheduled === true || allScheduled) return { ...base, phase: 'scheduled', startsAt };
  if (newest === null) return { ...base, phase: 'awaiting' };
  return { ...base, phase: 'monitoring' };
};

export interface PourCounts {
  /** Outside a temperature or differential limit now (fresh readings). */
  outsideLimits: number;
  /** Offline at the last fetch. */
  notReporting: number;
  delayed: number;
  /** Locations whose logger reports a fault, whatever their freshness (as
   *  the Control Panel's WarningCount). */
  sensor: number;
  /** Concrete channels reporting within their limits ("N OK"). */
  ok: number;
  /** Reporting, but no limit was checked: concrete channels with no limit
   *  set, and site and humidity channels. */
  reporting: number;
  /** Scheduled or awaiting their first reading. */
  waiting: number;
  ended: number;
  lowBattery: number;
  total: number;
  /** Some concrete location has limits: the outside-limits chip shows. */
  hasLimits: boolean;
}

/** The status strip's counts from every location's health. */
export const pourCounts = (
  healths: ReadonlyArray<LocationHealth> | ReadonlyMap<string, LocationHealth>,
): PourCounts => {
  const list: LocationHealth[] = Array.isArray(healths)
    ? (healths as LocationHealth[])
    : Array.from((healths as ReadonlyMap<string, LocationHealth>).values());
  const counts: PourCounts = {
    outsideLimits: 0,
    notReporting: 0,
    delayed: 0,
    sensor: 0,
    ok: 0,
    reporting: 0,
    waiting: 0,
    ended: 0,
    lowBattery: 0,
    total: list.length,
    hasLimits: list.some((h) => h.concrete && h.hasLimits),
  };
  list.forEach((h) => {
    if (h.sensorIssue) counts.sensor += 1;
    if (h.batteryLow && h.alert !== 'ended' && h.alert !== 'scheduled') counts.lowBattery += 1;
    switch (h.alert) {
      case 'over_limit':
      case 'under_limit':
        counts.outsideLimits += 1;
        break;
      case 'offline':
        counts.notReporting += 1;
        break;
      case 'delayed':
        counts.delayed += 1;
        break;
      case 'ok':
        counts.ok += 1;
        break;
      case 'no_limits':
      case 'reporting':
        counts.reporting += 1;
        break;
      case 'awaiting':
      case 'scheduled':
        counts.waiting += 1;
        break;
      case 'ended':
        counts.ended += 1;
        break;
      default:
        break;
    }
  });
  return counts;
};

// ---------------------------------------------------------------------------
// The Overview's sections
// ---------------------------------------------------------------------------

export interface OverviewEntry {
  location: LocationStatus;
  health: LocationHealth;
}

/** One card: a location, or the channels of one ambient Temp & Humidity
 *  logger together (one row per channel). */
export type OverviewItem =
  | { kind: 'location'; key: string; entry: OverviewEntry }
  | { kind: 'ambient'; key: string; loggerCode: string; entries: OverviewEntry[] };

/** The function groups of orderByFunction. */
export type OverviewGroupKey = 'maturity' | 'temperature' | 'ambient' | 'weather';

export interface OverviewSection {
  /** 'attention', a function group, or 'all' on a single-function pour. */
  key: 'attention' | 'all' | OverviewGroupKey;
  /** The English heading (the screen translates by key); null on a
   *  single-function pour. */
  label: string | null;
  items: OverviewItem[];
  /** More than 8 locations: one compact row per card after the attention
   *  group. */
  compact: boolean;
}

/** Above this many locations the cards after Needs attention are compact. */
export const COMPACT_ABOVE = 8;

const GROUP_KEYS: Record<string, OverviewGroupKey> = {
  'Concrete Maturity': 'maturity',
  'Temperature Only': 'temperature',
  'Ambient Temp & Humidity': 'ambient',
  'Weather Station': 'weather',
};

/** The logger a channel belongs to: its code before the last '-'
 *  ("AU001930-4" is logger "AU001930"). */
export const loggerCodePrefix = (code: string | null | undefined): string => {
  const text = (code ?? '').trim();
  const dash = text.lastIndexOf('-');
  return dash > 0 ? text.slice(0, dash) : text;
};

const itemSeverity = (item: OverviewItem): number =>
  item.kind === 'location'
    ? item.entry.health.severity
    : Math.min(...item.entries.map((e) => e.health.severity));

/**
 * The Overview: "Needs attention" first (outside limits, offline, delayed,
 * sensor issue, low battery; stable within a level), then the rest grouped
 * by function in orderByFunction's order (headed only on multi-function
 * pours). The channels of one ambient Temp & Humidity logger share a card;
 * it joins Needs attention when any of its channels does.
 */
export const overviewSections = (
  locations: ReadonlyArray<LocationStatus>,
  healthById: ReadonlyMap<string, LocationHealth>,
): OverviewSection[] => {
  const { ordered, groupLabels } = orderByFunction([...locations]);
  const compact = locations.length > COMPACT_ABOVE;

  // Cards in order, each with the function group label of its first row.
  const items: { item: OverviewItem; label: string | null; index: number }[] = [];
  const ambientItems = new Map<string, OverviewEntry[]>();
  let label: string | null = null;
  ordered.forEach((location, i) => {
    if (groupLabels.has(i)) label = groupLabels.get(i) ?? null;
    const health = healthById.get(location.id);
    if (!health) return;
    const entry: OverviewEntry = { location, health };
    const prefix = isAmbientChannel(location) ? loggerCodePrefix(location.logger_code) : '';
    if (prefix) {
      const held = ambientItems.get(prefix);
      if (held) {
        held.push(entry);
        return;
      }
      const entries = [entry];
      ambientItems.set(prefix, entries);
      items.push({ item: { kind: 'ambient', key: `ambient-${prefix}`, loggerCode: prefix, entries }, label, index: i });
      return;
    }
    items.push({ item: { kind: 'location', key: location.id, entry }, label, index: i });
  });

  const sections: OverviewSection[] = [];
  const attention = items
    .filter(({ item }) => itemSeverity(item) !== NO_ATTENTION)
    .sort((a, b) => itemSeverity(a.item) - itemSeverity(b.item) || a.index - b.index);
  if (attention.length > 0) {
    sections.push({ key: 'attention', label: 'Needs attention', items: attention.map((a) => a.item), compact: false });
  }
  items
    .filter(({ item }) => itemSeverity(item) === NO_ATTENTION)
    .forEach(({ item, label: groupLabel }) => {
      const key: OverviewSection['key'] = groupLabel ? GROUP_KEYS[groupLabel] ?? 'all' : 'all';
      const last = sections[sections.length - 1];
      if (last && last.key === key) {
        last.items.push(item);
      } else {
        sections.push({ key, label: groupLabel, items: [item], compact });
      }
    });
  return sections;
};

// ---------------------------------------------------------------------------
// Maturity
// ---------------------------------------------------------------------------

export interface NextTargetInfo {
  /** Every target of the location is reached. */
  allReached: boolean;
  /** The next target (the first not reached, by maturity). */
  target: Target | null;
  /** Its configured purpose ("Stripping"); null without one. */
  name: string | null;
  /** Its strength with the unit ("25.0 MPa"); '' without one. */
  strengthText: string;
  /** Its maturity threshold. */
  maturity: number | null;
  /** Maturity still to go (0 or less once passed). */
  remaining: number | null;
  /** Current maturity as a share of the target's, 0 to 1. */
  progress: number | null;
  /** targetStatusText: "Not yet: 560.0 °C·hrs to go", "Not yet recorded:
   *  maturity has passed this target", "Not reached: ... short when
   *  monitoring ended". */
  status: TargetStatus | null;
  reached: number;
  total: number;
}

export interface NextTargetOptions {
  /** The company strength unit (for strengthText). */
  strengthUnit?: string | null;
  /** The pour's IANA timezone. */
  timezoneIana?: string | null;
  /** Monitoring has ended for the location. */
  ended?: boolean;
}

/**
 * A maturity location's next target: the first target not reached, by
 * target maturity, with its status from targetStatusText. Null when the
 * location has no targets.
 */
export const nextTarget = (
  loc: LocationStatus,
  meta: MaturityDescriptor,
  { strengthUnit, timezoneIana, ended = false }: NextTargetOptions = {},
): NextTargetInfo | null => {
  const targets = (loc.targets ?? []).filter((t) => finite(t.target_maturity) !== null);
  if (targets.length === 0) return null;
  const sorted = [...targets].sort(
    (a, b) => a.target_maturity - b.target_maturity || (a.target_strength ?? 0) - (b.target_strength ?? 0),
  );
  const reached = sorted.filter((t) => t.achieved).length;
  const next = sorted.find((t) => !t.achieved);
  if (!next) {
    return {
      allReached: true,
      target: null,
      name: null,
      strengthText: '',
      maturity: null,
      remaining: null,
      progress: 1,
      status: null,
      reached,
      total: sorted.length,
    };
  }
  const current = finite(loc.current_ea);
  const status = targetStatusText({
    achieved: false,
    targetMaturity: next.target_maturity,
    currentMaturity: current,
    concreteStart: loc.ea_start,
    monitoringStart: loc.logging_started,
    ended,
    meta,
    timezoneIana,
  });
  return {
    allReached: false,
    target: next,
    name: targetPurpose(next.name),
    strengthText: targetStrengthText(next.target_strength, strengthUnit),
    maturity: next.target_maturity,
    remaining: next.target_maturity - (current ?? 0),
    progress: next.target_maturity > 0 ? Math.min(1, Math.max(0, (current ?? 0) / next.target_maturity)) : null,
    status,
    reached,
    total: sorted.length,
  };
};

/** A location's strength as shown on the phone: the strength reading when
 *  the pour has a usable curve, else its maturity ("EA at 20 °C: 36.2 hrs"). */
const strengthOrMaturityText = (
  loc: LocationStatus,
  pour: Pick<PourSummary, 'has_curve' | 'has_calibration' | 'calibration_check' | 'strength_unit'> | null | undefined,
  meta: MaturityDescriptor,
): string => {
  const strengthShown = pourHasCurve(pour) !== false && pour?.calibration_check?.status !== 'mismatch';
  if (strengthShown) {
    const reading = resolveStrengthReading(loc.strength, loc.current_strength);
    const text = formatStrengthReading(reading, resolveStrengthUnit(pour?.strength_unit), '');
    if (text) return text;
  }
  return formatMaturityWithIndex(loc.current_ea, meta, 1, '');
};

interface TargetGroup {
  name: string | null;
  strength: number | null;
  maturity: number;
  carriers: LocationStatus[];
  reachedBy: Set<string>;
}

/** The pour's targets matched across its maturity locations: by purpose,
 *  else by their place in maturity order. */
const targetGroups = (carriers: ReadonlyArray<LocationStatus>): Map<string, TargetGroup> => {
  const groups = new Map<string, TargetGroup>();
  carriers.forEach((loc) => {
    [...(loc.targets ?? [])]
      .filter((t) => finite(t.target_maturity) !== null)
      .sort((a, b) => a.target_maturity - b.target_maturity)
      .forEach((t, rank) => {
        const name = targetPurpose(t.name);
        const key = name ? `name:${name}` : `rank:${rank}`;
        const group = groups.get(key) ?? {
          name,
          strength: finite(t.target_strength) !== null && (t.target_strength as number) > 0 ? (t.target_strength as number) : null,
          maturity: t.target_maturity,
          carriers: [],
          reachedBy: new Set<string>(),
        };
        group.maturity = Math.min(group.maturity, t.target_maturity);
        group.carriers.push(loc);
        if (t.achieved) group.reachedBy.add(loc.id);
        groups.set(key, group);
      });
  });
  return groups;
};

export interface PourMaturitySummary {
  /** Every maturity location reached every target it carries. */
  allReached: boolean;
  /** The pour's next target: the first, by maturity, that not every
   *  location carrying it has reached. */
  nextTargetName: string | null;
  /** Its strength (company unit); null without one. */
  nextTargetValue: number | null;
  /** Its strength with the unit ("25.0 MPa"); '' without one. */
  strengthText: string;
  nextTargetMaturity: number | null;
  /** Locations that reached it, of those carrying it. */
  reached: number;
  total: number;
  /** The location furthest from it (least maturity). */
  lowest: { id: string; name: string; strengthText: string } | null;
}

/**
 * The status strip's maturity line for the pour: "Next: Stripping 25 MPa ·
 * 2 of 4 locations reached · lowest: Loc 3 at 18.2 MPa". Targets are
 * matched across locations by purpose (else by their place in maturity
 * order). Null when no maturity location has targets.
 */
export const pourMaturitySummary = (
  locations: ReadonlyArray<LocationStatus>,
  pour?: PourSummary | null,
): PourMaturitySummary | null => {
  const carriers = locations.filter((l) => locationHasMaturity(l) && (l.targets ?? []).length > 0);
  if (carriers.length === 0) return null;
  const meta = maturityMeta(pour);
  const unit = resolveStrengthUnit(pour?.strength_unit);
  const groups = targetGroups(carriers);

  const next = Array.from(groups.values())
    .sort((a, b) => a.maturity - b.maturity)
    .find((g) => g.reachedBy.size < g.carriers.length);
  if (!next) {
    return {
      allReached: true,
      nextTargetName: null,
      nextTargetValue: null,
      strengthText: '',
      nextTargetMaturity: null,
      reached: carriers.length,
      total: carriers.length,
      lowest: null,
    };
  }
  const behind = next.carriers.filter((l) => !next.reachedBy.has(l.id));
  const lowest = behind.reduce<LocationStatus | null>(
    (low, l) => (low === null || (finite(l.current_ea) ?? 0) < (finite(low.current_ea) ?? 0) ? l : low),
    null,
  );
  return {
    allReached: false,
    nextTargetName: next.name,
    nextTargetValue: next.strength,
    strengthText: targetStrengthText(next.strength, unit),
    nextTargetMaturity: next.maturity,
    reached: next.reachedBy.size,
    total: next.carriers.length,
    lowest: lowest
      ? {
          id: lowest.id,
          name: lowest.name || lowest.logger_code || lowest.id,
          strengthText: strengthOrMaturityText(lowest, pour, meta),
        }
      : null,
  };
};

export interface TargetCount {
  /** Its purpose ("Stripping"); null without one. */
  name: string | null;
  /** Its strength with the unit ("25.0 MPa"); '' without one. */
  strengthText: string;
  /** Its maturity threshold (the lowest across the locations carrying it). */
  maturity: number;
  /** Locations that reached it, of those carrying it. */
  reached: number;
  total: number;
}

/**
 * Every target of the pour with how many of the locations carrying it have
 * reached it ("Stripping 25 MPa: 12 of 12"), in maturity order. A count
 * over all the locations, never one location's value. Empty when no
 * maturity location has targets.
 */
export const pourTargetCounts = (
  locations: ReadonlyArray<LocationStatus>,
  pour?: PourSummary | null,
): TargetCount[] => {
  const carriers = locations.filter((l) => locationHasMaturity(l) && (l.targets ?? []).length > 0);
  const unit = resolveStrengthUnit(pour?.strength_unit);
  return Array.from(targetGroups(carriers).values())
    .sort((a, b) => a.maturity - b.maturity)
    .map((g) => ({
      name: g.name,
      strengthText: targetStrengthText(g.strength, unit),
      maturity: g.maturity,
      reached: g.reachedBy.size,
      total: g.carriers.length,
    }));
};

// ---------------------------------------------------------------------------
// Mass concrete
// ---------------------------------------------------------------------------

export interface WorstDifferential {
  /** |ΔT| °C. */
  value: number;
  limit: number | null;
  operator: string | null;
  limitName: string | null;
  over: boolean;
  /** When the pair's readings were taken (naive UTC). */
  at: string | null;
  locationId: string;
  locationName: string;
  partnerName: string | null;
}

/**
 * The pour's worst differential against its limit, from the dashboard's
 * differentials (site state): one over its limit first (furthest over),
 * then the one closest to its limit, then the largest without a limit.
 * Null when no concrete location has a current differential.
 */
export const worstDifferential = (locations: ReadonlyArray<LocationStatus>): WorstDifferential | null => {
  const candidates: WorstDifferential[] = [];
  locations.forEach((loc) => {
    if (isSiteConditionsChannel(loc)) return;
    (loc.differentials ?? []).forEach((d) => {
      const value = finite(d.value);
      if (value === null) return;
      candidates.push({
        value,
        limit: finite(d.limit_value),
        operator: d.operator,
        limitName: d.limit_name,
        over: !!d.over,
        at: d.value_at,
        locationId: loc.id,
        locationName: loc.name || loc.logger_code || loc.id,
        partnerName: d.partner_name,
      });
    });
  });
  const rank = (c: WorstDifferential): [number, number, number] => [
    c.over ? 1 : 0,
    c.limit !== null ? 1 : 0,
    c.limit !== null ? c.value - c.limit : c.value,
  ];
  candidates.sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    return rb[0] - ra[0] || rb[1] - ra[1] || rb[2] - ra[2];
  });
  return candidates[0] ?? null;
};

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

/** The Charts tab's options. */
export type MobileChartKey = 'temperature' | 'strength' | 'maturity' | 'humidity' | 'evaporation';

const hasStrengthValue = (loc: LocationStatus): boolean =>
  resolveStrengthReading(loc.strength, loc.current_strength)?.value != null;

/**
 * The Charts tab's options, only those with data, in this order:
 *  - Temperature: some concrete (non site-conditions) location has read;
 *  - Strength: a maturity pour with a usable curve, no 9.2 mismatch, and
 *    some maturity location with a strength value;
 *  - Maturity (TTF / EA): a maturity pour with some maturity location's
 *    maturity;
 *  - Temp & RH: some ambient Temp & Humidity channel has read;
 *  - Evaporation: an evaporation or Onsite Weather Conditions pour (legacy
 *    mass concrete too) with weather data and a concrete location.
 * Empty before any reading.
 */
export const chartPickerOptions = (
  data: Pick<DashboardResponse, 'pour' | 'locations' | 'weather'>,
  flags: PourFunctionFlags,
): MobileChartKey[] => {
  const locations = data.locations ?? [];
  if (!locations.some((l) => !!l.last_read)) return [];
  const concrete = locations.filter((l) => !isSiteConditionsChannel(l));
  const options: MobileChartKey[] = [];
  if (concrete.some((l) => !!l.last_read)) options.push('temperature');
  if (flags.maturity) {
    const maturityLocations = locations.filter(locationHasMaturity);
    const mismatch = data.pour?.calibration_check?.status === 'mismatch';
    if (!mismatch && pourHasCurve(data.pour) === true && maturityLocations.some(hasStrengthValue)) {
      options.push('strength');
    }
    if (maturityLocations.some((l) => finite(l.current_ea) !== null)) options.push('maturity');
  }
  if (locations.some((l) => isAmbientChannel(l) && !!l.last_read)) options.push('humidity');
  if ((flags.evaporation || flags.weather) && data.weather != null && concrete.length > 0) {
    options.push('evaporation');
  }
  return options;
};

/** What each Charts tab option loads per location. */
export const OVERLAY_NEED: Record<MobileChartKey, ChartSeriesKey[]> = {
  temperature: ['temp'],
  humidity: ['temp'],
  strength: ['ea'],
  maturity: ['ea'],
  evaporation: ['eva'],
};

/** The overlay metric (overlayModel / MultiLocationChart) of an option. */
export const OVERLAY_METRIC: Record<MobileChartKey, MultiChartMetric> = {
  temperature: 'temperature',
  humidity: 'temperature',
  strength: 'strength',
  maturity: 'maturity',
  evaporation: 'weather',
};

/** The locations a Charts tab option overlays, as the desktop sections:
 *  concrete channels for Temperature and Evaporation, ambient Temp &
 *  Humidity channels for Temp & RH, maturity locations for Strength and
 *  Maturity. */
export const overlayLocations = (
  locations: ReadonlyArray<LocationStatus>,
  key: MobileChartKey,
): LocationStatus[] => {
  if (key === 'humidity') return locations.filter(isAmbientChannel);
  if (key === 'strength' || key === 'maturity') return locations.filter(locationHasMaturity);
  return locations.filter((l) => !isSiteConditionsChannel(l));
};

/** The overlay's series id of a location (overlayModel's). */
export const overlaySeriesId = (locationId: string): string => `location-${locationId}`;

/** Locations shown at first on a big pour's overlay. */
export const OVERLAY_DEFAULT_SHOWN = 8;
/** The hottest locations always shown at first on a big pour's overlay. */
const OVERLAY_HOTTEST_SHOWN = 5;

/**
 * The overlay series hidden at first: none up to 8 locations; above that,
 * every location but those needing attention and the 5 hottest now.
 */
export const defaultHiddenOverlaySeries = (
  locations: ReadonlyArray<LocationStatus>,
  healthById: ReadonlyMap<string, LocationHealth>,
): Set<string> => {
  if (locations.length <= OVERLAY_DEFAULT_SHOWN) return new Set<string>();
  const shown = new Set<string>();
  locations.forEach((l) => {
    if (healthById.get(l.id)?.attention) shown.add(l.id);
  });
  [...locations]
    .filter((l) => finite(l.current_temp) !== null)
    .sort((a, b) => (b.current_temp as number) - (a.current_temp as number))
    .slice(0, OVERLAY_HOTTEST_SHOWN)
    .forEach((l) => shown.add(l.id));
  return new Set(locations.filter((l) => !shown.has(l.id)).map((l) => overlaySeriesId(l.id)));
};

const hasPoints = (points: ReadonlyArray<ReadonlyArray<number | null>> | null | undefined): boolean =>
  (points ?? []).some((p) => p[0] != null && p[1] != null);

/**
 * The Location tab's chart kinds with points in the location's chart data,
 * in this order: temp (temperature or %RH, and the tank), diff (concrete
 * channels with differential data), strength and maturity (maturity
 * locations of a maturity pour; strength not with a 9.2 mismatch or
 * without a curve), eva (evaporation or weather pours, concrete channels).
 */
export const locationChartKinds = (
  chartData: ChartDataResponse | null | undefined,
  location: LocationStatus,
  flags: PourFunctionFlags,
  pour?: Pick<PourSummary, 'has_curve' | 'has_calibration' | 'calibration_check'> | null,
): PhoneLocationChartKind[] => {
  if (!chartData) return [];
  const kinds: PhoneLocationChartKind[] = [];
  const site = isSiteConditionsChannel(location);
  if (hasPoints(chartData.temp) || hasPoints(chartData.tankTemp)) kinds.push('temp');
  if (!site && Object.values(chartData.difftemp ?? {}).some((points) => hasPoints(points))) kinds.push('diff');
  if (flags.maturity && locationHasMaturity(location)) {
    const strengthAllowed = !pour || (pour.calibration_check?.status !== 'mismatch' && pourHasCurve(pour) !== false);
    if (strengthAllowed && (chartData.ea ?? []).some((p) => p[0] != null && p[1] != null)) kinds.push('strength');
    if ((chartData.ea ?? []).some((p) => p[0] != null && (p[2] ?? p[1]) != null)) kinds.push('maturity');
  }
  if (!site && (flags.evaporation || flags.weather) && hasPoints(chartData.eva)) kinds.push('eva');
  return kinds;
};

/**
 * What the Location tab loads for a location (useLocationChart's need):
 * nothing for a weather station (its readings are not charted per
 * location) or before the first reading; else the temperature (and the
 * tank on a concrete channel), the differentials of a concrete channel
 * (when the dashboard lists partners, or does not say), the maturity of a
 * maturity location, and the evaporation of an evaporation or weather pour.
 */
export const locationChartNeed = (
  location: LocationStatus,
  flags: PourFunctionFlags,
  /** The summary's weather row is present. False skips evaporation, which
   *  is computed from weather readings; undefined keeps the function rule. */
  hasWeather?: boolean,
): ChartSeriesKey[] => {
  if (isWeatherLocation(location) || !location.last_read) return [];
  if (isSiteConditionsChannel(location)) return ['temp'];
  const need: ChartSeriesKey[] = ['temp', 'tank'];
  if (location.differentials === undefined || location.differentials.length > 0) need.push('diff');
  if (flags.maturity && locationHasMaturity(location)) need.push('ea');
  if ((flags.evaporation || flags.weather) && hasWeather !== false) need.push('eva');
  return need;
};

/** Location ids to names, for naming differential partners. */
export const locationNames = (locations: ReadonlyArray<LocationStatus>): Record<string, string> =>
  Object.fromEntries(locations.map((l) => [l.id, l.name || l.logger_code || l.id]));
